import {
  ActionReducerMapBuilder,
  createEntityAdapter,
  createSelector,
  createSlice,
  EntityState,
  PayloadAction
} from '@reduxjs/toolkit';

import { IFormValues } from 'src/pages/ReviewPage/components/BulkSelectDoc/BulkSelectDoc';
import { RootState } from 'src/store';
import { Status as StatusTypes } from 'src/ts/enums';
import { IProof, ProofResultInsufficientReasonType } from 'src/ts/interfaces';
import { NAME } from './constants';

import {
  getIncomeVerificationProofsByEntryIdThunk,
  getProofByIdThunk,
  getProofsByEntryIdThunk,
  getProofTextBreakupThunk,
  updateProofThunk
} from './services';

const proofsAdapter = createEntityAdapter<IProof>({
  selectId: (proof) => proof.id
});

interface StateData extends EntityState<IProof> {
  status: StatusTypes;
  error?: string | null;
}

const initialState = proofsAdapter.getInitialState({
  status: StatusTypes.IDLE,
  error: null
});

const proofsSlice = createSlice({
  name: NAME,
  initialState,
  reducers: {
    reset: () => {
      return {
        ...initialState
      };
    },
    onUpdateCheckProof: (
      state: StateData,
      action: PayloadAction<{ proofId: string; isChecked: boolean }>
    ) => {
      const { proofId, isChecked } = action.payload;
      proofsAdapter.updateOne(state, { id: proofId, changes: { isChecked } });
    },
    onUpdateCheckProofs: (state: StateData, action: PayloadAction<{ isChecked: boolean }>) => {
      const { isChecked } = action.payload;
      const data = proofsAdapter.getSelectors().selectAll(state);

      for (const entries of data) {
        proofsAdapter.updateOne(state, { id: entries.id, changes: { isChecked } });
      }
    },
    setLoadingProof: (
      state: StateData,
      action: PayloadAction<{ proofId: string; isLoading: boolean }>
    ) => {
      const { proofId, isLoading } = action.payload;
      proofsAdapter.updateOne(state, { id: proofId, changes: { isLoading } });
    },
    updateLocalProofState: (
      state: StateData,
      action: PayloadAction<{ id: string; formValues: IFormValues }>
    ) => {
      const { id, formValues } = action.payload;
      const { olvReasons, ...rest } = formValues;
      // ITL-2870: CUSTOM is a scalar sentinel, never a real array member; a
      // poisoned DB row can seed olvReasons=['CUSTOM'], so drop it before use.
      const cleanedOlvReasons = olvReasons?.filter((reason) => reason !== 'CUSTOM');
      const hasOlvReasons = cleanedOlvReasons && cleanedOlvReasons.length > 0;
      proofsAdapter.updateOne(state, {
        id: id,
        changes: {
          ...(rest.document != null && { type: rest.document }),
          isChecked: false,
          ...rest,
          ...(hasOlvReasons
            ? {
                result_insufficient_reasons: cleanedOlvReasons,
                // ITL-3094: mirror proof_view.ex flattening — a genuinely single
                // reason is the scalar literal, CUSTOM otherwise. Diverging here
                // made the local entity say CUSTOM after a PASS submit while the
                // server said PENDING_EMPLOYER_VERIFICATION, tripping the wrong
                // lock. A stripped CUSTOM entry (ITL-2870 poisoned row) keeps the
                // CUSTOM scalar ("Custom — see note").
                result_insufficient_reason: (cleanedOlvReasons.length === 1 &&
                olvReasons?.length === 1
                  ? cleanedOlvReasons[0]
                  : 'CUSTOM') as ProofResultInsufficientReasonType
              }
            : rest.result === 'UNDETERMINED' &&
              rest.resultInsufficientReason &&
              rest.resultInsufficientReason !== 'NONE' &&
              rest.resultInsufficientReason !== 'CUSTOM'
            ? {
                result_insufficient_reasons: [
                  rest.resultInsufficientReason
                ] as IProof['result_insufficient_reasons'],
                result_insufficient_reason:
                  rest.resultInsufficientReason as ProofResultInsufficientReasonType
              }
            : // ITL-2870: CUSTOM is a scalar-only sentinel ("Custom — see note"):
            // keep the scalar, exclude it from the array. Mirrors useProofUpdateAside
            // (both FE producers agree). See ITL-2922.
            rest.result === 'UNDETERMINED' && rest.resultInsufficientReason === 'CUSTOM'
            ? {
                result_insufficient_reasons: null,
                result_insufficient_reason: 'CUSTOM' as ProofResultInsufficientReasonType
              }
            : {
                result_insufficient_reasons: null,
                result_insufficient_reason: undefined
              })
        }
      });
    }
  },
  extraReducers: (builder) => {
    getProofsByEntryIdReducer(builder);
    getIncomeVerificationProofsByEntryIdReducer(builder);
    getProofReducer(builder);
    updateProofReducer(builder);
    getProofTextBreakupReducer(builder);
  }
});

const getProofsByEntryIdReducer = (builder: ActionReducerMapBuilder<StateData>) => {
  builder.addCase(getProofsByEntryIdThunk.pending, (state) => {
    state.status = StatusTypes.LOADING;
    state.error = null;
  });
  builder.addCase(getProofsByEntryIdThunk.fulfilled, (state, action) => {
    const response = action.payload;
    const proofs = (response?.data || []) as IProof[];

    proofsAdapter.upsertMany(
      state,
      proofs?.map((proof, index) => {
        const position = (index + 1).toString();
        return {
          ...proof,
          fileName: 'Document ' + position
        };
      }) || []
    );
    state.status = StatusTypes.SUCCESS;
  });
  builder.addCase(getProofsByEntryIdThunk.rejected, (state, action) => {
    state.status = StatusTypes.ERROR;
    state.error = action.error?.message;
  });
};

const getIncomeVerificationProofsByEntryIdReducer = (
  builder: ActionReducerMapBuilder<StateData>
) => {
  builder.addCase(getIncomeVerificationProofsByEntryIdThunk.pending, (state) => {
    state.status = StatusTypes.LOADING;
    state.error = null;
  });
  builder.addCase(getIncomeVerificationProofsByEntryIdThunk.fulfilled, (state, action) => {
    const response = action.payload;
    const proofs = (response?.data || []) as IProof[];

    proofsAdapter.upsertMany(
      state,
      proofs?.map((proof, index) => {
        const position = (index + 1).toString();
        return {
          ...proof,
          fileName: 'Document ' + position
        };
      }) || []
    );

    state.status = StatusTypes.SUCCESS;
  });
  builder.addCase(getIncomeVerificationProofsByEntryIdThunk.rejected, (state, action) => {
    state.status = StatusTypes.ERROR;
    state.error = action.error?.message;
  });
};

const getProofReducer = (builder: ActionReducerMapBuilder<StateData>) => {
  builder.addCase(getProofByIdThunk.pending, (state) => {
    state.status = StatusTypes.LOADING;
    state.error = null;
  });
  builder.addCase(getProofByIdThunk.fulfilled, (state, action) => {
    const response = action.payload;
    const proof = (response?.data || {}) as IProof;

    proofsAdapter.addOne(state, proof);
    state.status = StatusTypes.SUCCESS;
  });
  builder.addCase(getProofByIdThunk.rejected, (state, action) => {
    state.status = StatusTypes.ERROR;
    state.error = action.error?.message;
  });
};

const updateProofReducer = (builder: ActionReducerMapBuilder<StateData>) => {
  builder.addCase(updateProofThunk.pending, (state, action) => {
    const { id } = action.meta.arg;
    if (id) {
      proofsAdapter.updateOne(state, { id, changes: { isLoading: true } });
    }
    state.error = null;
  });
  builder.addCase(updateProofThunk.fulfilled, (state, action) => {
    const response = action.payload;
    const proof = (response?.data || {}) as IProof;

    proofsAdapter.upsertOne(state, { ...proof, isLoading: false });

    state.status = StatusTypes.SUCCESS;
  });
  builder.addCase(updateProofThunk.rejected, (state, action) => {
    const { id } = action.meta.arg;
    if (id) {
      proofsAdapter.updateOne(state, { id, changes: { isLoading: false } });
    }
    state.status = StatusTypes.ERROR;
    state.error = action.error?.message;
  });
};

const getProofTextBreakupReducer = (builder: ActionReducerMapBuilder<StateData>) => {
  builder.addCase(getProofTextBreakupThunk.pending, (state, action) => {
    const { id } = action.meta.arg;
    if (id) {
      const changes = {
        text_breakups_status: StatusTypes.LOADING,
        text_breakups_error: undefined
      };
      proofsAdapter.updateOne(state, { id, changes });
    }
  });

  builder.addCase(getProofTextBreakupThunk.fulfilled, (state, action) => {
    const response = action.payload;
    const { id } = action.meta.arg;
    if (id) {
      const changes = {
        text_breakups_status: StatusTypes.SUCCESS,
        text_breakups_error: undefined,
        text_breakups: response.text_breakups
      };
      proofsAdapter.updateOne(state, { id, changes });
    }
  });

  builder.addCase(getProofTextBreakupThunk.rejected, (state, action) => {
    const { id } = action.meta.arg;
    if (id) {
      const changes = {
        text_breakups_status: StatusTypes.ERROR,
        text_breakups_error: action.error.message
      };
      proofsAdapter.updateOne(state, { id, changes });
    }
  });
};

// Filters by the passed entry id directly. The previous version looked the
// entry up in the entries slice first, silently returning [] on pages that
// never load the entry there (ITL-2996 VOA flow) — the proofs were fetched
// but invisible.
const selectAllByEntryId = createSelector(
  [
    proofsAdapter.getSelectors((state: RootState) => state[NAME]).selectAll,
    (_state: RootState, entryId: string) => entryId
  ],
  (proofs, entryId) => proofs?.filter((proof) => proof.entry_id === entryId) || []
);

export const selectors = {
  ...proofsAdapter.getSelectors((state: RootState) => state[NAME]),
  selectAllByEntryId,
  status: (state: RootState) => state[NAME].status
};

export const { reducer, actions } = proofsSlice;
